import { ImagePaths } from '../../lib/imageUrls.js';

export const sc500CloudAndAiSecurityEngineer = {
    id: 22,
    slug: 'sc-500-cloud-and-ai-security-engineer',
    ogImage: '/og/courses/sc-500-cloud-and-ai-security-engineer.jpg',
    courseCode: 'SC-500-001',
    featured: false,
    title: 'SC-500: Cloud & AI Security Engineer',
    subtitle: 'Protect cloud and AI workloads with enterprise-grade security',

    valueProposition: 'Secure hybrid cloud and generative AI environments by mastering identity, networking, data protection and threat response. Learn practical techniques to harden AI workloads against emerging threats while aligning to Australian security frameworks and preparing for SC-500 certification.',

    duration: '36 hours',
    students: 'New',
    level: 'Advanced',
    organization: 'Microsoft',
    image: ImagePaths.COURSE_1,
    shortDescription: 'SC-500-001 — Cloud and AI security engineering covering Entra ID, Sentinel, Defender, and AI threat mitigation for Australian senior security engineers.',

    price: {
      aud: 599,
      currency: 'AUD',
      earlyBird: 549
    },

    stripeConfig: {
    priceId: 'price_1T9bEAKu1nyc322UeBdpZnWm',
    productId: 'prod_U7qwV2AJG5fjxc'
  },

    snapshot: {
      whoItsFor: [
        'Cloud security engineers and architects',
        'AI security specialists and ML engineers',
        'DevOps teams managing hybrid cloud environments',
        'Security operations professionals using Sentinel/Defender',
        'Organisations implementing generative AI securely',
        'Candidates preparing for Microsoft SC-500 certification'
      ],
      accreditation: 'Preparation for Microsoft SC-500: Cloud and AI Security Engineer certification'
    },

    logistics: {
      classSize: 'Maximum 12 participants to allow deep technical lab work',
      requirements: [
        'Computer with modern web browser and administrative access',
        'Active Microsoft Azure subscription (pay-as-you-go or sponsorship)',
        'Visual Studio Code or preferred IDE installed',
        'Reliable internet connection (minimum 20 Mbps)',
        'Experience with PowerShell or Bash scripting',
        'Familiarity with Azure portal and CLI',
        'Background in cloud networking and security concepts'
      ]
    },

    overview: 'This intensive 36-hour course dives deep into securing cloud and AI infrastructures. You\'ll configure robust identity and access controls with Microsoft Entra ID, implement network segmentation and private links, and protect data at rest and in transit using Key Vault and encryption. The curriculum covers AI-specific threat mitigation such as prompt injection and model poisoning, and shows how to build security operations workflows with Microsoft Sentinel and Defender for Cloud. Throughout the course you\'ll apply Australian Privacy Principles, Essential Eight, ISO 27001 and other local compliance frameworks. Ideal for senior engineers and security professionals tasked with defending AI and hybrid cloud services.',

    outcomes: [
      'Design and implement secure Entra ID identity architectures with MFA and granular RBAC for AI resources',
      'Harden network perimeters using VNETs, Private Links, firewalls and network policies',
      'Apply encryption strategies for data at rest and in transit, integrating with Azure Key Vault',
      'Secure containerised AI compute environments and manage secrets for ML workloads',
      'Identify and mitigate AI-specific threats including prompt injection, data poisoning and model inversion',
      'Build security monitoring and incident response pipelines using Sentinel and Defender for Cloud',
      'Align security controls with APPs, Essential Eight, ISO 27001 and Australian cloud regulations',
      'Prepare for the SC-500 certification via hands-on exam-style scenarios'
    ],

    audience: [
      'Cloud security engineers and architects',
      'AI/ML security specialists',
      'DevSecOps and platform engineers',
      'Security operations centre (SOC) analysts',
      'IT leaders responsible for cloud and AI governance',
      'SC-500 exam candidates and auditors'
    ],

    prerequisites: [
      'Strong Azure fundamentals (AZ-900 or equivalent)',
      'Experience with Entra ID, networking and security concepts',
      'Familiarity with scripting (PowerShell/Bash) and infrastructure as code',
      'Basic understanding of machine learning lifecycle concepts',
      'Access to an Azure subscription with appropriate permissions'
    ],

    modules: [
      {
        title: 'Secure Identity & Access',
        duration: '8 hours',
        topics: [
          'Designing Entra ID tenants and managing users/groups',
          'Implementing multi-factor authentication and conditional access',
          'Configuring privilege identity management and least-privilege RBAC',
          'Service principals, managed identities and access tokens for AI workloads',
          'Audit logging and access reviews for compliance'
        ]
      },
      {
        title: 'Secure Networking & Infrastructure',
        duration: '8 hours',
        topics: [
          'Virtual network design for AI services',
          'Private Link, Service Endpoints and firewall policies',
          'Network security groups and Azure Firewall configuration',
          'Segmentation for data sovereignty and Essential Eight alignment',
          'Monitoring network traffic with NSG flow logs and Traffic Analytics'
        ]
      },
      {
        title: 'Securing AI Data & Applications',
        duration: '8 hours',
        topics: [
          'Encryption at rest with Storage Service Encryption and Customer-Managed Keys',
          'TLS/HTTPS, VPN and ExpressRoute for data in transit',
          'Key Vault integration and secret management patterns',
          'Protecting containerised AI (AKS, ACI) and functions',
          'Secure configuration of ML compute instances and notebooks'
        ]
      },
      {
        title: 'AI-Specific Threat Protection',
        duration: '6 hours',
        topics: [
          'Understanding the OWASP Top 10 for LLMs',
          'Defending against prompt injection and jailbreaks',
          'Detecting and preventing data poisoning and model inversion',
          'Implementing input validation and sanitisation pipelines',
          'Using content safety services and prompt shields'
        ]
      },
      {
        title: 'Security Operations & Response',
        duration: '6 hours',
        topics: [
          'Configuring Microsoft Sentinel for cloud/AI telemetry',
          'Creating analytics rules and playbooks for AI incidents',
          'Integrating Defender for Cloud alerts and recommendations',
          'Automating response with Logic Apps and Azure Functions',
          'Reporting and compliance dashboards for APPs and ISO audits'
        ]
      }
    ],

    format: {
      type: 'Live online with hands-on labs',
      includes: [
        '36 hours of instructor-led training across 4–5 weeks',
        'Intensive labs in a controlled Azure environment',
        'Comprehensive course workbook and security templates',
        'Exam-style scenarios and practice questions for SC-500',
        'Access to security checklists and compliance guides',
        'Certificate of completion',
        '12 months access to course materials'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Four full days or eight half-day sessions available to suit busy teams'
    },

    // Pathway relationships
    pathways: [
      'ai-security-pathway',
      'ai-governance-pathway'
    ],

    skillsTaught: [
      'Cloud security for AI workloads',
      'AI threat mitigation (prompt injection, model poisoning)',
      'Azure Sentinel and Defender configuration',
      'Data protection and compliance (APPs, ISO 27001)',
      'AI incident response and monitoring'
    ],

    hasCapstone: true,

    nextCourses: [
      'ai-governance-risk-compliance'
    ],

    faqs: [
      {
        question: 'Who should attend this course?',
        answer: 'Senior cloud and security engineers, AI specialists, and SOC analysts responsible for protecting hybrid cloud and generative AI systems will benefit most. It\'s designed for professionals who already manage or audit AI workloads.'
      },
      {
        question: 'Do I need prior certification to enrol?',
        answer: 'While there is no formal prerequisite certification, strong knowledge of Azure security (e.g. AZ-900/AZ-104) and hands-on experience is expected. This is an advanced course, not for beginners.'
      },
      {
        question: 'How does the course address Australian regulatory requirements?',
        answer: 'We weave Australian Privacy Principles, Essential Eight, ISO 27001 and Notifiable Data Breaches considerations throughout the labs. You\'ll practise configuring controls that satisfy local mandates and cyber.gov.au guidance.'
      },
      {
        question: 'Will I use real Azure security tools?',
        answer: 'Yes—labs leverage Microsoft Sentinel, Defender for Cloud, Entra ID and other services in a live subscription. You\'ll build rules, playbooks and dashboards identical to those used in production.'
      },
      {
        question: 'How much will the Azure labs cost me?',
        answer: 'The training subscription is pre-configured with cost-limits and cleanup scripts. Most participants incur less than $50 AUD in lab charges; we provide detailed cost-management guidance.'
      },
      {
        question: 'Can the content be customised for my organisation?',
        answer: 'Absolutely. We can tailor labs and examples to your cloud architecture, compliance requirements and threat models. In-house delivery is ideal for teams needing focused, relevant material.'
      },
      {
        question: 'What support is available after the course?',
        answer: 'Trainees receive 12 months access to all resources.'
      }
    ]
};